import { CheckIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { UserAPIs } from '@/api';
import { Creatives } from '@/creatives';
import { CreativesList } from '@/creatives/list';
import { useAuth } from '@/hooks/useAuth';
import { Invite } from '@/invite';
import { Login } from '@/login';
import { Templates } from '@/templates';
import { TemplateList } from '@/templates/list';

import type { User } from '@/api/types/user';
import { UserType } from '@/api/types/user';
import { HomeLayout } from './home-layout';

interface DefaultRouteProps {
    path: string;
}

const DefaultRoute: FC<DefaultRouteProps> = ({ path }) => {
    return <Navigate to={path} replace={true} />;
};

interface ProtectedRouteProps {
    component: ReactNode;
    allowedUserTypes?: UserType[];
    redirectToIfNotAllowed?: string;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({
    component,
    allowedUserTypes,
    redirectToIfNotAllowed,
}) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const { checkAuth } = useAuth();

    const authCheck = useCallback(async () => {
        const authStatus = await checkAuth();
        if (authStatus) {
            const user = await UserAPIs.getCurrentUser();
            setUser(user);
        }
        setIsAuthenticated(authStatus);
    }, [checkAuth]);

    useEffect(() => {
        authCheck();
    }, [authCheck]);

    if (isAuthenticated === null) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace={true} />;
    }

    if (allowedUserTypes && user && !allowedUserTypes.includes(user.type)) {
        if (!redirectToIfNotAllowed) {
            throw new Error('redirectToIfNotAllowed is required when allowedUserTypes is provided');
        }
        return <Navigate to={redirectToIfNotAllowed} replace={true} />;
    }

    return <>{component}</>;
};

const LoginRoute: FC = () => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const { checkAuth } = useAuth();

    const authCheck = useCallback(async () => {
        const authStatus = await checkAuth();
        if (authStatus) {
            const user = await UserAPIs.getCurrentUser();
            setUser(user);
        }
        setIsAuthenticated(authStatus);
    }, [checkAuth]);

    useEffect(() => {
        authCheck();
    }, [authCheck]);

    if (isAuthenticated === null) {
        return <div>Loading...</div>;
    }

    if (isAuthenticated && user) {
        if (user.type === UserType.MEMBER) {
            return <Navigate to="/creatives" replace={true} />;
        }
        return <Navigate to="/templates" replace={true} />;
    }

    return <Login />;
};

export const App: FC = () => {
    return (
        <div className="flex flex-col w-screen h-screen">
            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={true}
                newestOnTop={true}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable={false}
                pauseOnHover
                theme="colored"
                icon={({ type, theme }) => {
                    switch (type) {
                        case 'error':
                            return <XMarkIcon className="stroke-red-500" />;
                        case 'success':
                            return <CheckIcon className="stroke-green-500" />;
                        default:
                            return null;
                    }
                }}
            />
            <Routes>
                <Route index element={<DefaultRoute path="/login" />} />
                <Route path="/login" element={<LoginRoute />} />
                <Route path="/invite" element={<Invite />} />
                <Route element={<ProtectedRoute component={<HomeLayout />} />}>
                    <Route
                        path="/templates"
                        element={
                            <ProtectedRoute
                                component={<TemplateList />}
                                allowedUserTypes={[UserType.ADMIN, UserType.DESIGNER]}
                                redirectToIfNotAllowed="/creatives"
                            />
                        }
                    />
                    <Route path="/creatives" element={<ProtectedRoute component={<CreativesList />} />} />
                </Route>
                <Route
                    path="/templates/*"
                    element={
                        <ProtectedRoute
                            component={<Templates />}
                            allowedUserTypes={[UserType.ADMIN, UserType.DESIGNER]}
                            redirectToIfNotAllowed="/creatives"
                        />
                    }
                />
                <Route
                    path="/creatives/*"
                    element={
                        <ProtectedRoute
                            component={<Creatives />}
                            allowedUserTypes={[UserType.ADMIN, UserType.DESIGNER, UserType.MEMBER]}
                            redirectToIfNotAllowed="/templates"
                        />
                    }
                />
            </Routes>
        </div>
    );
};
